<template>
  <div class="app-dashboard">
    <div class="dashboard-container">
      <div class="row row-cols-1 row-cols-md-2 dashboard-row">
        <div class="col dashboard-col">
          <chart-card
            :title="t('Status Distribution')"
            :loading="loading"
            :is-empty="!hasStatusData"
            :empty-text="t('No status data available')"
            compact
          >
            <pie-chart
              :data="getStatusData"
              :loading="loading"
              :is-empty="!hasStatusData"
              :doughnut="true"
              :small="true"
              title="Status"
              :height="200"
            />
          </chart-card>
        </div>

        <div class="col dashboard-col">
          <chart-card
            :title="t('Package Types')"
            :loading="loading"
            :is-empty="!hasPackageTypeData"
            :empty-text="t('No package type data available')"
            compact
          >
            <pie-chart
              :data="getPackageTypeData"
              :loading="loading"
              :is-empty="!hasPackageTypeData"
              :doughnut="true"
              :small="true"
              title="Package Types"
              :height="200"
            />
          </chart-card>
        </div>

        <!-- <div class="col dashboard-col">
          <chart-card
            :title="t('Post Type Distribution')"
            :loading="loading"
            :is-empty="!hasPostTypeData"
            :empty-text="t('No post type data available')"
            compact
          >
            <pie-chart
              :data="getPostTypeData"
              :loading="loading"
              :is-empty="!hasPostTypeData"
              :doughnut="true"
              :legend-position="'right'"
              title="Post Types"
              :height="200"
            />
          </chart-card>
        </div> -->
      </div>

      <div class="row row-cols-1 row-cols-md-2 dashboard-row">
        <div class="col dashboard-col">
          <chart-card
            :title="t('Weight Range Statistics')"
            :loading="loading"
            :is-empty="!hasWeightRangeData"
            :empty-text="t('No weight range data available')"
            compact
          >
            <bar-chart
              :categories="getWeightRangeCategories"
              :series="getWeightRangeSeries"
              :loading="loading"
              :is-empty="!hasWeightRangeData"
              :horizontal="true"
              :value-min="0"
              :area="true"
              :height="200"
            />
          </chart-card>
        </div>

        <div class="col dashboard-col">
          <chart-card
            :title="t('Week Days Popularity')"
            :loading="loading"
            :is-empty="!hasWeekDaysData"
            :empty-text="t('No week days data available')"
            compact
          >
            <line-chart
              :categories="getWeekDaysCategories"
              :series="getWeekDaysSeries"
              :loading="loading"
              :is-empty="!hasWeekDaysData"
              :colors="['#FF9F43']"
              :height="200"
            />
          </chart-card>
        </div>
      </div>

      <div class="row row-cols-1 row-cols-lg-3 dashboard-row">
        <div class="col dashboard-col">
          <list-card
            :title="t('Popular Destinations')"
            :items="getDestinationCountriesItems"
            :loading="loading"
            :empty-text="t('No destination countries data available')"
            badge-variant="primary"
            compact
          />
        </div>

        <div class="col dashboard-col">
          <list-card
            :title="t('Popular Providers')"
            :items="getProvidersItems"
            :loading="loading"
            :empty-text="t('No providers data available')"
            badge-variant="success"
            compact
          />
        </div>

        <div class="col dashboard-col">
          <div class="stats-container">
            <!-- <stat-card
              :title="t('Total Consignments')"
              :value="
                dashboardData.consignment_count ? dashboardData.consignment_count.total : null
              "
              :empty-text="t('No consignment data')"
              icon="PackageIcon"
              color="primary"
              :loading="loading"
              compact
            /> -->

            <stat-card
              :title="t('Delivered Countries Count')"
              :value="
                dashboardData.active_country_count ? dashboardData.active_country_count.total : null
              "
              :empty-text="t('No country data')"
              icon="GlobeIcon"
              color="success"
              :loading="loading"
              compact
            />
          </div>
        </div>
      </div>

      <div class="row dashboard-row">
        <div class="col dashboard-col">
          <shipment-world-map :routes="dashboardData.consignments_map_view || []" :zoom="6" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useService from '@/auth/service/useService';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { StatCard, ChartCard, ListCard, BarChart, PieChart, LineChart } from '../../components';
import ShipmentWorldMap from '../../components/ShipmentWorldMap.vue';

export default {
  components: {
    StatCard,
    ChartCard,
    ListCard,
    BarChart,
    PieChart,
    LineChart,
    ShipmentWorldMap
  },
  mixins: [helper],
  props: {
    selectedFilter: {
      type: String,
      default: 'thisYear'
    },
    shipmentDirection: {
      type: [Number, String],
      default: null
    }
  },
  setup() {
    const { t } = useI18nUtils();
    return {
      t
    };
  },
  data() {
    return {
      loading: false,
      dashboardData: {},
      filterOptions: [
        { value: 'today', text: this.$t('Today') },
        { value: 'yesterday', text: this.$t('Yesterday') },
        { value: 'thisWeek', text: this.$t('This Week') },
        { value: 'lastWeek', text: this.$t('Last Week') },
        { value: 'thisMonth', text: this.$t('This Month') },
        { value: 'lastMonth', text: this.$t('Last Month') },
        { value: 'thisYear', text: this.$t('This Year') },
        { value: 'lastYear', text: this.$t('Last Year') }
      ]
    };
  },
  computed: {
    fetchKey() {
      return [this.selectedFilter, this.shipmentDirection].join('|');
    },
    hasWeightRangeData() {
      return (
        this.dashboardData.consignment_weight_range_statistic &&
        Array.isArray(this.dashboardData.consignment_weight_range_statistic.labels) &&
        Array.isArray(this.dashboardData.consignment_weight_range_statistic.data) &&
        this.dashboardData.consignment_weight_range_statistic.labels.length > 0 &&
        this.dashboardData.consignment_weight_range_statistic.data.length > 0
      );
    },
    getWeightRangeCategories() {
      if (!this.hasWeightRangeData) return [];
      return this.dashboardData.consignment_weight_range_statistic.labels;
    },
    getWeightRangeSeries() {
      if (!this.hasWeightRangeData) return [];

      const stats = this.dashboardData.consignment_weight_range_statistic;

      return [
        {
          name: this.$t('Count'),
          data: stats.data
        }
      ];
    },

    hasDestinationCountriesData() {
      return (
        this.dashboardData.most_popular_destination_countries &&
        this.dashboardData.most_popular_destination_countries.length > 0
      );
    },
    getDestinationCountriesItems() {
      if (!this.hasDestinationCountriesData) return [];
      return this.dashboardData.most_popular_destination_countries.map(item => ({
        name: this.$t(item.name),
        value: item.total
      }));
    },

    hasProvidersData() {
      return (
        this.dashboardData.most_popular_providers &&
        this.dashboardData.most_popular_providers.length > 0
      );
    },
    getProvidersItems() {
      if (!this.hasProvidersData) return [];
      return this.dashboardData.most_popular_providers.map(item => ({
        name: item.name,
        value: item.count
      }));
    },

    hasWeekDaysData() {
      return (
        this.dashboardData.week_days_popularity &&
        this.dashboardData.week_days_popularity.labels &&
        this.dashboardData.week_days_popularity.data
      );
    },
    getWeekDaysCategories() {
      if (!this.hasWeekDaysData) return [];
      return this.dashboardData.week_days_popularity.labels.map(label => this.$t(label));
    },
    getWeekDaysSeries() {
      if (!this.hasWeekDaysData) return [];
      return [
        {
          name: this.$t('Popularity'),
          data: this.dashboardData.week_days_popularity.data
        }
      ];
    },

    hasPackageTypeData() {
      return (
        this.dashboardData.consignment_package_type &&
        this.dashboardData.consignment_package_type.length > 0
      );
    },
    getPackageTypeData() {
      if (!this.hasPackageTypeData) return [];
      return this.dashboardData.consignment_package_type.map(item => ({
        name: this.getPackageTypeName(item.packageType) || item.description || item.name,
        value: item.total
      }));
    },

    hasPostTypeData() {
      return (
        this.dashboardData.consignment_post_type &&
        this.dashboardData.consignment_post_type.length > 0
      );
    },
    getPostTypeData() {
      if (!this.hasPostTypeData) return [];
      return this.dashboardData.consignment_post_type.map(item => ({
        name: item.description,
        value: item.total
      }));
    },

    hasStatusData() {
      return (
        this.dashboardData.consignment_count_by_status &&
        this.dashboardData.consignment_count_by_status.length > 0
      );
    },
    getStatusData() {
      if (!this.hasStatusData) return [];

      const sortedStatuses = [...this.dashboardData.consignment_count_by_status].sort(
        (a, b) => b.count - a.count
      );

      return sortedStatuses.map(item => ({
        name:
          (item.key && this.$te && this.$te(item.key) ? this.$t(item.key) : null) ||
          this.$t(item.info) ||
          item.info,
        value: item.count
      }));
    }
  },
  methods: {
    async loadData() {
      this.loading = true;

      try {
        const args = { currentTab: 1, dateBetween: this.selectedFilter };
        if (this.shipmentDirection !== null && this.shipmentDirection !== undefined) {
          args.transportDirection = this.shipmentDirection;
        }
        const res = await useService.getDashboard(args);
        this.dashboardData = res.data;
      } catch (error) {
        this.dashboardData = {};
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    fetchKey: {
      immediate: true,
      handler() {
        if (this.shipmentDirection == null) return;
        this.loadData();
      }
    }
  }
};
</script>
